import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useState } from "react";

import { trpc } from "@blis/trpc/react";

import { Button } from "@/components/ui/button";
import { Checkbox, CheckboxField } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Description } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { Text } from "@/components/ui/text";
import { toast } from "@/components/ui/toast";
import { useDialogProps } from "../detail/state";

export function CloneDialog({ accession }: { accession: { id: number } }) {
  const [checked, setChecked] = useState(false);
  const dialogProps = useDialogProps("clone");
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { mutate, isPending } = trpc.accession.clone.useMutation({
    onSuccess: (accession) => {
      toast.success("Accession cloned");
      dialogProps.onOpenChange?.(false);
      if (pathname.startsWith("/subjects")) {
        navigate({
          to: "/subjects/$id/accession",
          params: { id: accession.subjectId },
          search: (prev) => ({
            ...prev,
            accessionId: accession.id,
          }),
          replace: true,
        });
      } else {
        navigate({ to: "/accessions/$id", params: { id: accession.id } });
      }
    },
    onError(err) {
      toast.error("Error cloning accession", {
        description: err.message,
      });
    },
  });

  const confirmClicked = () => {
    mutate({ id: accession.id, cloneSamples: checked });
  };

  return (
    <Modal isOpen={dialogProps.open} onOpenChange={dialogProps.onOpenChange}>
      <Dialog>
        <DialogHeader>
          <DialogTitle>Clone accession</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Text className="mb-2">
            Are you sure you want to clone this accession?
          </Text>
          <CheckboxField>
            <Checkbox
              isSelected={checked}
              onChange={(flag) => setChecked(flag)}
              // description="Cloning will copy all donor fields but will exclude the original tests and samples. If the same set of samples are to be used, check the box below."
            >
              Clone samples and receive time
            </Checkbox>
            <Description>
              Cloning will copy all donor fields but will exclude the original
              tests and samples. If the same set of samples are to be used,
              check the box below.
            </Description>
          </CheckboxField>
        </DialogBody>
        <DialogFooter>
          <Button
            onPress={confirmClicked}
            isPending={isPending}
            isDisabled={isPending}
            type="button"
          >
            Confirm
          </Button>
          <DialogCloseButton>Cancel</DialogCloseButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
